import React, { useState } from 'react';
import Button from '../../library/Button/button';
import { IPPHeading } from '../../library/Heading/Heading';
import { useNavigate } from 'react-router-dom';
/**
 * AutoLogoutPopup Component
 *
 * This React component displays a popup message to inform the user that their session
 * is about to be expired. It provides two buttons for the user to logout
 * or continue the session again. The `onConfirm` prop is a callback function that should be triggered when
 * the user clicks the "No" button. The `onCancel` prop is also a callback function that should be triggered
 * when the user clicks the "Yes" button.
 *
 * @param {object} props - The component props.
 * @param {function} props.onConfirm - Callback function to handle the YES button click.
 * @param {function} props.onCancel - Callback function to handle the NO button click.
 * @param {function} props.isSessionExpired - Callback function to track the session timeout limit.
 *
 * @returns {JSX.Element} - Returns the JSX element for the auto logout popup.
 */

interface AutoLogoutPopupProps {
  onConfirm: () => void;
  onCancel: () => void;
  isSessionExpired: boolean;
}

const AutoLogoutPopup: React.FC<AutoLogoutPopupProps> = ({
  onConfirm,
  onCancel,
  isSessionExpired
}) => {
  const navigate = useNavigate();
  const [showTimeoutNotice, setShowTimeoutNotice] = useState(false);

  // Handle clicks: if session expired, show timeout notice popup
  // else, call appropriate handlers
  const handleYesClick = () => {
    if (isSessionExpired) {
      setShowTimeoutNotice(true);
    } else {
      onCancel();
    }
  };

  const handleNoClick = () => {
    if (isSessionExpired) {
      setShowTimeoutNotice(true);
    } else {
      onConfirm();
    }
  };

  const handleCloseTimeoutNotice = () => {
    // Redirect to login page
    void navigate('/login');
  };

  if (showTimeoutNotice) {
    return (
      <div className="popup fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50">
        <div className="relative flex flex-col items-center justify-center bg-white p-8 rounded-lg max-w-md text-center shadow-lg">
          <button
            onClick={handleCloseTimeoutNotice}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-200 transition-colors duration-200"
            aria-label="Close popup"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l6 6M15 9l-6 6" />
            </svg>
          </button>
          <br />
          <IPPHeading
            headerText="Your session is timed out."
            className="text-3xl mb-4 font-semibold"
          />
          <IPPHeading
            headerText="Please close the popup to login again."
            className="text-xl mb-8 text-gray-700"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="popup fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50">
      <div className="flex flex-col items-center justify-center bg-white p-8 rounded-lg max-w-md text-center shadow-lg">
        <IPPHeading
          headerText="Your session is about to expire."
          className="text-3xl mb-4 font-semibold"
        />
        <IPPHeading
          headerText="Do you want to continue your session?"
          className="text-xl mb-8 text-gray-700"
        />
        <div className="flex gap-6">
          <Button
            className="border border-black text-black bg-transparent font-semibold py-2 px-8 rounded-lg hover:bg-black hover:text-white transition-colors duration-300"
            onclick={handleYesClick}
          >
            Yes
          </Button>
          <Button
            className="border border-red text-red bg-transparent font-semibold py-2 px-8 rounded-lg shadow hover:bg-red hover:text-white hover:border-none transition-colors duration-300"
            onclick={handleNoClick}
          >
            No
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AutoLogoutPopup;
